import { displayUnitOf } from "@heraion/shared";
import { useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  latestByField,
  type GlucoseUnit,
  type Sex,
} from "@heraion/shared";
import {
  MeasurementForm,
  MeasurementHistory,
  MeasurementTrends,
  MetabolicCard,
  QuickGlucoseCard,
  resolveTheme,
  useClockOn,
  type UiTheme,
} from "@heraion/ui";
import { confirmViaWindow, notifyViaWindow } from "./confirm";
import { useThemePalette } from "./theme";
import { useProfileDoc } from "./useProfileDoc";
import { useMeasurementsWeb } from "./useMeasurementsWeb";
import { useMetabolicWeb } from "./useMetabolicWeb";
import { useGlucoseWeb } from "./useGlucoseWeb";

// Me → Corpo → Measurements on the web console: the SAME shared entry
// Form + History + Trends mobile mounts (MeasurementTrends was lifted to
// @heraion/ui in S3, 2026-06-29 — the chart is the shared WagmiLineChart, no
// native-only piece left here), over the online useMeasurementsWeb resource.
// Confirms/alerts come from the host-wide web seam (./confirm), shared with
// Maxes and the coach target editor rather than re-implemented here.

export function MeasurementsScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const { doc, loading: profileLoading } = useProfileDoc();
  const {
    items,
    loading,
    loadFailed,
    hasMore,
    loadMore,
    loadingMore,
    create,
    update,
    remove,
  } = useMeasurementsWeb();
  const { glucose, nutrition, loading: metabolicLoading } = useMetabolicWeb();
  const { readings: glucoseReadings, create: createGlucose } = useGlucoseWeb();
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const styles = makeStyles(theme);

  const units = displayUnitOf(doc);
  const sex = doc?.["sex"] as Sex | undefined;
  const glucoseUnit = (doc?.["glucoseUnit"] as GlucoseUnit | undefined) ?? "mmol";
  // Stable per data-load so the card's todayCarbs memo doesn't bust each render.
  const nowMs = useClockOn(nutrition);
  const byField = useMemo(() => latestByField(items), [items]);
  const editEntry = useMemo(
    () => items.find((m) => m.id === editId) ?? null,
    [items, editId],
  );

  const deleteEntry = async (id: string): Promise<boolean> => {
    if (id === editId) setEditId(null);
    return remove(id);
  };

  if (loading || profileLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      {loadFailed ? (
        <Text style={styles.error}>
          {t("mobile.body.loadError", {
            defaultValue: "Couldn't load measurements — check your connection.",
          })}
        </Text>
      ) : null}

      <MeasurementTrends
        items={items}
        sex={sex}
        units={units}
        locale={i18n.language}
        theme={theme}
        nowMs={Date.now()}
        hasMore={hasMore}
      />

      <QuickGlucoseCard
        theme={theme}
        unit={glucoseUnit}
        readings={glucoseReadings}
        nowMs={Date.now()}
        onSave={createGlucose}
      />

      <MetabolicCard
        glucose={glucose}
        nutrition={nutrition}
        glucoseUnit={glucoseUnit}
        loading={metabolicLoading}
        locale={i18n.language}
        nowMs={nowMs}
        theme={theme}
      />

      <MeasurementForm
        theme={theme}
        confirm={confirmViaWindow}
        notify={notifyViaWindow}
        lastByField={byField}
        units={units}
        saving={saving}
        editEntry={editEntry}
        onSave={async (payload) => {
          setSaving(true);
          try {
            return await create(payload);
          } finally {
            setSaving(false);
          }
        }}
        onUpdate={async (id, patch) => {
          setSaving(true);
          try {
            const ok = await update(id, patch);
            if (ok) setEditId(null);
            return ok;
          } finally {
            setSaving(false);
          }
        }}
        onDeleteEntry={deleteEntry}
        onCancelEdit={() => setEditId(null)}
      />

      <MeasurementHistory
        theme={theme}
        confirm={confirmViaWindow}
        items={items}
        units={units}
        locale={i18n.language}
        onEdit={(m) => setEditId(m.id)}
        onDelete={(id) => {
          void deleteEntry(id);
        }}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={() => {
          void loadMore();
        }}
      />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s4 },
    center: { flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: p.bg },
    error: { color: p.danger, fontSize: t.sizes.base },
  });
}
